import React, { useState, useMemo } from 'react'; // useMemo is imported
import { 
  Paper, Typography, Divider, Box, CircularProgress, 
  Tabs, Tab, Link as MuiLink, Pagination, Card, CardContent
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as regularIcons from '@fortawesome/free-regular-svg-icons'; // far
import * as solidIcons from '@fortawesome/free-solid-svg-icons';   // fas

const iconLibrary = {
  far: regularIcons,
  fas: solidIcons,
  // Add other prefixes like 'fab' for brands if needed
};

const getFaIconComponent = (iconClass) => {
  if (!iconClass) return null;
  
  // Example: ["far", "fa-check-square"]
  const [prefix, name] = iconClass.split(' '); 
  
  // Convert 'fa-check-square' to 'faCheckSquare'
  const iconName = name.replace('fa-', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  
  // Look up the icon in the correct library based on the prefix
  const iconObject = iconLibrary[prefix] ? iconLibrary[prefix][`fa${iconName.charAt(0).toUpperCase() + iconName.slice(1)}`] : null;

  if (iconObject) {
    return <FontAwesomeIcon icon={iconObject} fixedWidth style={{ marginRight: '8px' }} />;
  }
  return null;
};

// --- Configuration Constant for Document Pagination ---
const DOCS_PER_PAGE = 25; 

// --- Utility for Date Formatting (Unchanged) ---
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch {
    return dateString;
  }
};

// --- Helper Components (TabPanel, a11yProps, TimelineItem, DocumentItem Unchanged) ---
const TabPanel = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`content-tabpanel-${index}`}
    aria-labelledby={`content-tab-${index}`}
    style={{ width: '100%' }}
  >
    {value === index && (
      <Box sx={{ p: 2.5 }}>
        {children}
      </Box>
    )}
  </div>
);

const a11yProps = (index) => ({
  id: `content-tab-${index}`,
  'aria-controls': `content-tabpanel-${index}`,
});

const TimelineItem = ({ item }) => (
  // Replace Box with Card for a defined container look
  <Card 
    variant="outlined" // Use outlined for a subtle border
    sx={{ 
      mb: 2.5, 
      borderLeft: '4px solid #1976d2', // Add a vertical timeline indicator
      borderRadius: '4px 0 0 4px' // Keep border-radius only on the right
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Typography 
        variant="subtitle1" 
        sx={{ fontWeight: 600, mb: 0.5, textAlign: 'left', color: 'primary.main' }}
      >
        {getFaIconComponent(item.timeline_icon_class)}
        {item.timeline_name || 'Timeline Event'}
      </Typography>

      {/* Main text content */}
      <Box
        sx={{
          // Removed bottom border style as the Card provides separation
          '& p': { mb: 1, typography: 'body2', textAlign: 'left', lineHeight: 1.6 },
          '& ul, & ol': { pl: 3, typography: 'body2', lineHeight: 1.6 },
          py: 0.5 // small vertical padding for spacing
        }}
        dangerouslySetInnerHTML={{ __html: item.timeline_display_text || 'No description provided.' }}
      />

      {/* Byline */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ fontStyle: 'italic', textAlign: 'left', mt: 1, display: 'block' }}
      >
        {item.timeline_byline || 'Byline N/A'}
      </Typography>
    </CardContent>
  </Card>
);

const DocumentItem = ({ doc }) => {
  if (!doc?.document_scan?.document_id) return null;

  const downloadLink = `https://recordsrequest.lacity.org/documents/${doc.document_scan.document_id}/download?token=`;

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <MuiLink 
        href={downloadLink} 
        target="_blank" 
        rel="noopener"
        variant="body1"
        sx={{ fontWeight: 600, display: 'inline-block', mb: 0.5 }}
      >
        {doc.title || 'Untitled Document'}
      </MuiLink>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        <strong>Type:</strong> {doc.file_extension || 'N/A'} &nbsp; | &nbsp;
        <strong>Date:</strong> {formatDate(doc.upload_date)}
      </Typography>
    </Box>
  );
};

// --- Main Component ---
const RecordContentPanel = ({ selectedRecord, isLoading, error }) => {
  // --- 1. HOOK CALLS (MUST BE AT THE TOP LEVEL) ---
  const [currentTab, setCurrentTab] = useState(0);
  const [currentDocPage, setCurrentDocPage] = useState(1); 

  // Aggregation of documents using useMemo
  const { allDocumentsCount, documentList } = useMemo(() => {
    // Check for selectedRecord early within the useMemo callback
    if (!selectedRecord || !selectedRecord.documents || !Array.isArray(selectedRecord.documents)) {
        return { allDocumentsCount: 0, documentList: [] };
    }
      
    let count = 0;
    let list = [];
    
    selectedRecord.documents.forEach(docPage => {
        if (docPage) {
            // Take the total count from the first page (assuming it's accurate)
            if (count === 0 && docPage.all_documents_count !== undefined) {
                count = docPage.all_documents_count;
            }
            // Concatenate the document arrays
            if (Array.isArray(docPage.documents)) {
                list = list.concat(docPage.documents);
            }
        }
    });

    // Fallback if all_documents_count was missing but we still have documents
    if (count === 0) {
        count = list.length;
    }
    
    return { allDocumentsCount: count, documentList: list };
  }, [selectedRecord]); // Dependency on the entire selectedRecord object

  // --- 2. HANDLERS ---
  const handleTabChange = (e, newValue) => {
    setCurrentTab(newValue);
    // Reset document page when switching to the documents tab
    if (newValue === 1) {
        setCurrentDocPage(1);
    }
  };

  const handleDocPageChange = (e, value) => {
    setCurrentDocPage(value);
    const docListElement = document.getElementById('document-list-container');
    if (docListElement) docListElement.scrollTop = 0;
  };
  
  // --- 3. CONDITIONAL EARLY RETURNS ---
  if (isLoading) {
    return (
      <Paper elevation={3} sx={styles.containerCentered}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading record content...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={styles.containerScroll}>
        <Typography color="error" variant="body1" sx={{ textAlign: 'center', mt: 10 }}>
          Error loading content: {error}
        </Typography>
      </Paper>
    );
  }

  if (!selectedRecord) {
    return (
      <Paper elevation={3} sx={styles.containerScroll}>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mt: 10 }}>
          Select a record ID from the left to view its content.
        </Typography>
      </Paper>
    );
  }

  // --- 4. RENDER LOGIC (AFTER ALL HOOKS AND EARLY RETURNS) ---
  const requestHtml = selectedRecord.request_text || '<p>No request text available.</p>';
  const timelineData = [...(selectedRecord.timeline?.timeline || [])];
  const departments = selectedRecord.department_names || selectedRecord.department || 'N/A';
  const pointOfContact = selectedRecord.poc.email_or_name || 'N/A';
  
  const idLink = `https://recordsrequest.lacity.org/requests/${selectedRecord.id}`;
  
  // Document Pagination Logic
  const docPageCount = Math.ceil(documentList.length / DOCS_PER_PAGE);
  const docStartIndex = (currentDocPage - 1) * DOCS_PER_PAGE;
  const docEndIndex = docStartIndex + DOCS_PER_PAGE;
  const paginatedDocuments = documentList.slice(docStartIndex, docEndIndex);

  return (
    <Paper elevation={3} sx={styles.containerMain}>
      {/* --- HEADER --- */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Record ID:&nbsp;      
          <a 
            href={idLink}
            target="_blank" 
            rel="noopener noreferrer"
          >
            {selectedRecord.id}
          </a>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Date:</strong> {formatDate(selectedRecord.date)} &nbsp; | <strong>Status:</strong> {selectedRecord.status} | <strong>Documents:</strong> {allDocumentsCount}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Departments:</strong> {departments} | <strong>Point of Contact:</strong> {pointOfContact}
        </Typography>
      </Box>

      {/* --- REQUEST PANEL --- */}
      <Box sx={styles.section}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
          Request
        </Typography>
        <Box
          sx={styles.htmlBox}
          dangerouslySetInnerHTML={{ __html: requestHtml }}
        />
      </Box>

      {/* --- TABS --- */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="Record Details Tabs" 
            variant="fullWidth"
          >
            <Tab label="Timeline" {...a11yProps(0)} />
            <Tab label="Documents" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* --- TAB CONTENT --- */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }} id="document-list-container">
          {/* Timeline */}
          <TabPanel value={currentTab} index={0}>
            {timelineData.length ? (
              timelineData.map((item, i) => <TimelineItem key={i} item={item} />)
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                No timeline events found for this record.
              </Typography>
            )}
          </TabPanel>

          {/* Documents */}
          <TabPanel value={currentTab} index={1}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Associated Documents ({documentList.length} Total)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {documentList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                No documents available for this record.
              </Typography>
            ) : (
              <>
                {paginatedDocuments.map((doc, i) => <DocumentItem key={i} doc={doc} />)}
                
                {/* Pagination */}
                {docPageCount > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                        <Pagination
                            count={docPageCount}
                            page={currentDocPage}
                            onChange={handleDocPageChange}
                            color="primary"
                            size="small"
                        />
                    </Box>
                )}
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Showing {docStartIndex + 1} - {Math.min(docEndIndex, documentList.length)} of {documentList.length} documents.
                </Typography>
              </>
            )}
          </TabPanel>

          {/* Dates */}
          <TabPanel value={currentTab} index={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Key Dates
            </Typography>
            <Typography variant="body2">
              <strong>Received:</strong> {formatDate(selectedRecord.request_date)}
            </Typography>
          </TabPanel>

          {/* Staff */}
          <TabPanel value={currentTab} index={3}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Assigned Staff
            </Typography>
            <Typography variant="body2">
              <strong>Departments:</strong> {departments}
            </Typography>
            <Typography variant="body2">
              <strong>Point of Contact:</strong> {pointOfContact}
            </Typography>
          </TabPanel>
        </Box>
      </Box>
    </Paper>
  );
};

// --- Styles (Unchanged) ---
const styles = {
  containerMain: {
    p: 3,
    width: '780px',
    height: '760px',
    display: 'flex',
    flexDirection: 'column',
    typography: 'body2',
  },
  containerCentered: {
    p: 3,
    width: '820px',
    height: '70vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerScroll: {
    p: 3,
    width: '820px',
    height: '70vh',
    overflowY: 'auto',
  },
  section: {
    border: '1px solid #e0e0e0',
    borderRadius: 2,
    p: 2,
    mb: 3,
  },
  htmlBox: {
    maxHeight: 200,
    overflowY: 'auto',
    p: 1.5,
    backgroundColor: '#fafafa',
    borderRadius: 1,
    textAlign: 'left',
    '& p': { m: 0, lineHeight: 1.6 },
  },
};

export default RecordContentPanel;

/*
            <Tab label="Dates" {...a11yProps(2)} /> 
            <Tab label="Staff" {...a11yProps(3)} /> 
*/